import classNames from "classnames";
import clsx from "clsx";
import { GraphQLError } from "graphql";
import isEmpty from "is-empty";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Product } from "rsl-api-106/lib/types";

import SelectNumber from "@components/_Layout/Controls/SelectNumber";
import LoadingDots from "@components/_Layout/Loaders/LoadingDots";
import ReactComment from "@components/_Layout/ReactComment";
import SeeMore from "@components/_Layout/SeeMore";
import { useCart, useConfig, useSession } from "@hooks";
import { getCurrencySymbol } from "@utils/currencies";
import { getAbsoluteImagePath, replaceImgOnError } from "@utils/dom";
import { getDiscountByPrices } from "@utils/prices";

type ProductItemProps = {
  mode: "occupant" | "stay" | "global";
  product: Product;
  occupantGuiid?: number;
  stayGuiid?: number;
  defaultChecked?: boolean;
  defaultQuantity?: number;
  onAddProduct?: (
    productCode: string,
    productId: number,
    quantity: number,
    stayGuiid: number,
    callback: (err?: GraphQLError) => void
  ) => void;
  onAddOccupantProduct?: (
    productCode: string,
    productId: number,
    occupantGuiid: number,
    quantity: number,
    callback: (err?: GraphQLError) => void
  ) => void;
  showCriteria?: string | boolean;
};

const ProductItem2: React.FC<ProductItemProps> = ({
  mode,
  product,
  occupantGuiid,
  stayGuiid,
  defaultChecked = false,
  defaultQuantity = 0,
  onAddProduct,
  onAddOccupantProduct,
  showCriteria,
}) => {
  useEffect(() => {
    setEnabled(defaultChecked);
  }, [defaultChecked]);

  const { getLanguageVarValue } = useSession();
  const { cart, customerBooking } = useCart();
  const { mode: clientMode } = useConfig();
  const modeClient = clientMode ? clientMode : "vacanceole";

  const isFromFastCheckinOrCheckout = cart?.fromFastCheckinCheckout;

  const [enabled, setEnabled] = useState(defaultChecked);
  const [loading, setLoading] = useState<boolean>(false);

  const canAddProduct = (
    productCode: string,
    productId: number,
    quantity: number
  ) => {
    let canAdd = true;
    if (isFromFastCheckinOrCheckout) {
      const reservation = customerBooking;
      if (reservation) {
        for (let j = 0; j < (reservation?.stays?.length ?? 0); j++) {
          const stay = reservation?.stays?.[j];
          if (stay && (stay.guiid === stayGuiid || !stayGuiid)) {
            for (let k = 0; k < (stay?.occupants?.length ?? 0); k++) {
              let occupant = stay?.occupants?.[k];
              if (
                occupant &&
                (occupant.guiid === occupantGuiid || !occupantGuiid)
              ) {
                const occupantLineItems = occupant?.lineItems;
                if (occupantLineItems) {
                  const correspondingProduct = occupantLineItems.find(
                    (x) =>
                      x?.product?.code === productCode &&
                      x.product.id === productId
                  );
                  if (correspondingProduct) {
                    let baseQty = correspondingProduct.quantity ?? 0;
                    if (quantity < baseQty) {
                      canAdd = false;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    return canAdd;
  };

  const handleAddProduct = (
    productCode: string,
    productId: number,
    quantity: number
  ) => {
    const canEditThisProduct = canAddProduct(productCode, productId, quantity);
    if (!canEditThisProduct) {
      toast.error(
        getLanguageVarValue("LANG_neobooking_error_remove_product_fci")
      );
      return;
    }

    if (mode === "global" || (mode === "stay" && stayGuiid)) {
      handleAddGlobalProduct(productCode, productId, quantity, stayGuiid!!);
    }
    if (mode === "occupant" && occupantGuiid) {
      handleAddOccupantProduct(productCode, productId, occupantGuiid, quantity);
    }
  };

  const handleAddGlobalProduct = (
    productCode: string,
    productId: number,
    quantity: number,
    stayGuiid: number
  ) => {
    let selected = !enabled;
    quantity >= 1 ? (selected = true) : (selected = false);
    setLoading(true);
    onAddProduct &&
      onAddProduct(
        productCode,
        productId,
        !selected ? 0 : quantity,
        stayGuiid,
        (err) => {
          if (!err) setEnabled(selected);
          setLoading(false);
        }
      );
  };

  const handleAddOccupantProduct = (
    productCode: string,
    productId: number,
    occupantGuiid: number,
    quantity: number
  ) => {
    let selected = !enabled;
    quantity >= 1 ? (selected = true) : (selected = false);
    setLoading(true);
    onAddOccupantProduct &&
      onAddOccupantProduct(
        productCode,
        productId,
        occupantGuiid,
        !selected ? 0 : quantity,
        (err) => {
          if (!err) setEnabled(selected);
          setLoading(false);
        }
      );
  };

  const getPrestationCriteria = (): JSX.Element => {
    if (typeof showCriteria === "string") {
      const cms = product?.cmsCriterias?.find((x) => x?.code === showCriteria);
      let txt = "";
      if (cms) {
        txt = cms?.value ?? "";
      }
      return !isEmpty(txt) ? (
        <span className="tw-font-normal tw-text-lg">
          {` `}
          {txt}
        </span>
      ) : (
        <></>
      );
    } else {
      return <></>;
    }
  };

  return (
    <>
      <div className="tw-flex tw-gap-x-2 tw-bg-white tw-rounded-[26px] tw-border tw-border-[#707070]">
        <img
          className="tw-object-cover tw-w-[150px] md:tw-w-[150px] lg:tw-w-[200px] tw-min-h-[150px] md:tw-min-h-[200px] tw-rounded-[26px]"
          src={product.imagePath ?? ""}
          onError={replaceImgOnError}
        />
        <div className="tw-flex tw-flex-col tw-px-2 tw-py-4 tw-w-full">
          <p className="tw-font-bold tw-text-sm sm:tw-text-lg tw-font-spirit tw-mb-2">
            {!isEmpty(product.webLabel) ? product.webLabel : product.label}
          </p>
          {product?.cmsCriterias?.find((x) => x?.code === "WEBHTML")?.value ===
          "Oui" ? (
            <div
              dangerouslySetInnerHTML={{
                __html: product.description ?? "",
              }}
            ></div>
          ) : (
            <div className="tw-text-sm tw-text-black">
              <SeeMore
                popupTitle={
                  !isEmpty(product.webLabel)
                    ? product.webLabel!!
                    : product.label!!
                }
                defaultOpen={false}
                limit={160}
                content={product.description!!}
                btnElement={(opened, toggle) => (
                  <button
                    className={`${
                      modeClient === "vacanceole"
                        ? "tw-text-red-500 hover:tw-text-red-400"
                        : "tw-text_corseole-secondary"
                    } tw-font-medium tw-font-montserrat focus:tw-outline-none`}
                    onClick={toggle}
                  >
                    {opened ? (
                      <img
                        className="tw-w-4 tw-rotate-180"
                        src={getAbsoluteImagePath(
                          "/assets/img/icons/arrow-bottom.svg"
                        )}
                      ></img>
                    ) : (
                      getLanguageVarValue("LANG_neobooking_see_more")
                    )}{" "}
                  </button>
                )}
              ></SeeMore>
            </div>
          )}
          <div className="tw-flex tw-justify-between tw-h-full tw-items-end tw-mt-6">
            <div className="tw-text-xl tw-font-bold">
              <span className=" tw-font-spirit">
                {product?.price?.amount?.toFixed(2)}
                {getCurrencySymbol(product?.price?.currencyCode!!)}
              </span>
              {getPrestationCriteria()}
            </div>
            <div className="tw-flex tw-flex-col">
              {product.price?.amount !==
                product.priceWithoutDiscounts?.amount && (
                <div className="tw-hidden md:tw-flex tw-self-end tw-bg-yellow-200 tw-text-yellow-500 tw-font-bold sm:tw-text-xl tw-p-1">
                  -
                  {getDiscountByPrices(
                    product.price?.amount!!,
                    product.priceWithoutDiscounts?.amount!!
                  )}
                  %
                </div>
              )}
              <div className="tw-mt-2">
                <div className="tw-hidden md:tw-flex tw-justify-center tw-items-center">
                  <button
                    disabled={product.mandatory || defaultQuantity === 0}
                    onClick={() =>
                      (!product.mandatory || defaultQuantity !== 0) &&
                      handleAddProduct(
                        product.code,
                        product.id,
                        defaultQuantity - 1
                      )
                    }
                    className={clsx(
                      !canAddProduct(
                        product.code,
                        product.id,
                        defaultQuantity - 1
                      )
                        ? "tw-bg-v_blue-dark/50"
                        : "tw-bg-v_blue-dark",
                      "tw-font-spirit tw-border tw-border-v_blue-dark tw-text-white tw-rounded-2xl tw-w-[30px] tw-h-[30px] tw-flex tw-items-center tw-justify-center tw-text-3xl"
                    )}
                  >
                    -
                  </button>
                  <span
                    className={classNames(
                      "tw-w-[30px] tw-h-[30px] tw-text-[21px] tw-flex tw-items-center tw-justify-center tw-font-bold tw-text-v_blue-dark tw-font-spirit"
                    )}
                  >
                    {loading ? <LoadingDots /> : defaultQuantity}
                  </span>
                  <button
                    disabled={
                      product.mandatory || defaultQuantity === product.quantity
                    }
                    onClick={() =>
                      (!product.mandatory ||
                        defaultQuantity !== product.quantity) &&
                      handleAddProduct(
                        product.code,
                        product.id,
                        defaultQuantity + 1
                      )
                    }
                    className="disabled:tw-bg-v_blue-dark/50 tw-font-spirit tw-bg-v_blue-dark tw-border tw-border-v_blue-dark tw-text-white tw-rounded-2xl tw-w-[30px] tw-h-[30px] tw-flex tw-items-center tw-justify-center tw-text-3xl"
                  >
                    +
                  </button>
                </div>
                <div className="tw-block md:tw-hidden tw-w-full tw-text-right">
                  {product.price?.amount !==
                    product.priceWithoutDiscounts?.amount && (
                    <div className="tw-flex tw-self-end tw-bg-yellow-200 tw-text-yellow-500 tw-font-bold sm:tw-text-xl tw-p-1">
                      -
                      {getDiscountByPrices(
                        product.price?.amount!!,
                        product.priceWithoutDiscounts?.amount!!
                      )}
                      %
                    </div>
                  )}
                  <SelectNumber
                    disabled={loading}
                    value={defaultQuantity}
                    quantity={product.quantity!! + 1}
                    shape="default"
                    additionalClassname="tw-rounded tw-max-w-[75px]"
                    //start={0}
                    start={isFromFastCheckinOrCheckout ? defaultQuantity : 0}
                    onChange={(e) =>
                      !product.mandatory &&
                      handleAddProduct(
                        product.code,
                        product.id,
                        parseInt(e.target.value)
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

ProductItem2.propTypes = {
  //mode: PropTypes.oneOf(["occupant", "stay", "global"]).isRequired,
};

export default ProductItem2;
